import { HTMLAttributes, useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface OtpFormProps extends HTMLAttributes<HTMLDivElement> {
  VerifyOTP: (otp: string) => void;
}

const formSchema = z.object({
  otp: z.string().length(6, { message: "OTP must be 6 digits." }),
});

export function OtpForm({ className, VerifyOTP, ...props }: OtpFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(""));
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { otp: "" },
  });

  // Update form value when OTP values change
  useEffect(() => {
    const otpString = otpValues.join("");
    form.setValue("otp", otpString);

    // Enable/disable button based on OTP completion
    if (otpString.length === 6) {
      form.clearErrors("otp");
    }
  }, [otpValues, form]);

  function handleChange(value: string, index: number) {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    // Auto-focus next input if value is entered
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) {
    // Move to previous input on backspace if current input is empty
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }

    // Move to next input on Enter if current input has a value
    if (e.key === "Enter" && otpValues[index] && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  }

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true);

    try {
      await VerifyOTP(data.otp);
      // Reset form after successful verification
      setTimeout(() => {
        form.reset();
        setOtpValues(Array(6).fill(""));
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      form.setError("otp", { message: "❌ Incorrect OTP. Please try again." }); // Set error on OTP field
      setOtpError("❌ Incorrect OTP. Please try again."); // Show error message
    } finally {
      setIsLoading(false);
    }
  }

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // If pasted data is a 6-digit number, fill all inputs
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpValues(digits);
      // Focus last input after paste
      otpInputRefs.current[5]?.focus();
    }
  };

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                    <div className="flex items-center gap-2 justify-between">
                      {otpValues.map((digit, index) => (
                        <Input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleChange(e.target.value, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onPaste={(e) => handlePaste(e, index)}
                          ref={(el) => {
                            otpInputRefs.current[index] = el;
                          }}
                          className={cn(
                            "w-12 h-12 text-center text-lg",
                            form.formState.errors.otp || otpError
                              ? "border-red-500 focus:border-red-500"
                              : ""
                          )}
                        />
                      ))}
                    </div>
                  </FormControl>
                  {otpError && (
                    <p className="text-red-500 text-sm mt-1 animate-shake">
                      {otpError}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              className="mt-4 bg-[#022f5d] hover:bg-[#02305dcc] text-white"
              disabled={otpValues.join("").length !== 6}
              loading={isLoading}
              type="submit"
            >
              Verify
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
